import React, { useState } from 'react';
import { motion } from 'framer-motion';

export interface Tab {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  id?: string;
  type?: never;
}

export interface Separator {
  type: 'separator';
  title?: never;
  icon?: never;
  id?: never;
}

export type TabItem = Tab | Separator;

export interface ExpandedTabsProps {
  tabs: TabItem[];
  className?: string;
  activeId?: string | null;
  onTabClick?: (tab: Tab, index: number) => void;
}

export const ExpandedTabs: React.FC<ExpandedTabsProps> = ({
  tabs,
  className = '',
  activeId,
  onTabClick,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Determine current active index from activeId
  const activeIndex = tabs.findIndex(
    (t) => t.type !== 'separator' && t.id === activeId
  );
  const selectedIndex = activeIndex !== -1 ? activeIndex : 0;

  const handleSelect = (tab: Tab, index: number) => {
    if (onTabClick) onTabClick(tab, index);
  };

  const SeparatorComponent = () => (
    <div
      className="h-5 w-px bg-brand-light/40 dark:bg-brand-purple/30 mx-1 self-center"
      aria-hidden="true"
    />
  );

  return (
    <nav
      className={`flex items-center gap-1 rounded-full border border-brand-light/40 dark:border-brand-purple/30 bg-white/80 dark:bg-brand-dark-bg-secondary/90 p-1.5 shadow-lg shadow-brand-purple/5 backdrop-blur-xl transition-all duration-300 ${className}`}
      onMouseLeave={() => setHoveredIndex(null)}
    >
      {tabs.map((tab, index) => {
        if (tab.type === 'separator') {
          return <SeparatorComponent key={`separator-${index}`} />;
        }

        const Icon = tab.icon;
        const isSelected = selectedIndex === index;
        const isHovered = hoveredIndex === index;

        return (
          <button
            key={tab.title}
            type="button"
            onClick={() => handleSelect(tab, index)}
            onMouseEnter={() => setHoveredIndex(index)}
            data-cursor="button"
            aria-label={tab.title}
            className={`relative z-10 flex items-center gap-1 sm:gap-1.5 lg:gap-2 rounded-full px-2 md:px-2.5 lg:px-4 py-1 md:py-1.5 lg:py-2 text-[11px] md:text-xs lg:text-sm font-semibold transition-colors duration-200 focus:outline-none select-none cursor-pointer ${
              isSelected
                ? 'text-brand-purple dark:text-brand-light font-bold'
                : 'text-brand-navy/70 dark:text-brand-dark-text-muted hover:text-brand-purple dark:hover:text-white'
            }`}
          >
            {/* Active Pill Indicator with spring physics */}
            {isSelected && (
              <motion.div
                layoutId="navbar-active-pill"
                className="absolute inset-0 z-0 rounded-full bg-gradient-to-r from-brand-purple/15 to-brand-blue/10 dark:from-brand-purple/35 dark:to-brand-blue/25 border border-brand-purple/35 dark:border-brand-purple/50 shadow-sm"
                transition={{ type: 'spring', stiffness: 450, damping: 35 }}
              />
            )}

            {/* Hover Pill Feedback */}
            {isHovered && !isSelected && (
              <motion.div
                layoutId="navbar-hover-pill"
                className="absolute inset-0 z-0 rounded-full bg-brand-purple/5 dark:bg-brand-purple/15"
                transition={{ type: 'spring', stiffness: 450, damping: 35 }}
              />
            )}

            <span className="relative z-10 flex items-center gap-1 sm:gap-1.5 lg:gap-2">
              <Icon className={`h-3.5 w-3.5 md:h-3.5 md:w-3.5 lg:h-[18px] lg:w-[18px] flex-shrink-0 transition-transform duration-200 ${isSelected ? 'scale-110 text-brand-purple dark:text-brand-light' : ''}`} />
              <span className="whitespace-nowrap tracking-tight sm:tracking-normal">
                {tab.title}
              </span>
            </span>
          </button>
        );
      })}
    </nav>
  );
};

export default ExpandedTabs;
